class SlideCart extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('#SlideCart');
    this.debounceTimer = null;
    this.giftUpdating = false;
    this.refreshPromise = null;

    this.bindEvents();
    this.bindCartIcon();
    this.subscribeToCartUpdates();
    this.initScrollShadows();
  }

  initScrollShadows() {
    const wrapper = this.modal.querySelector('.slide-cart__body-wrapper');
    const body = this.modal.querySelector('.slide-cart__body');
    if (!wrapper || !body) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = body;
      wrapper.classList.toggle('slide-cart__body-wrapper--shadow-top', scrollTop > 0);
      wrapper.classList.toggle('slide-cart__body-wrapper--shadow-bottom', scrollTop + clientHeight < scrollHeight - 1);
    };

    body.addEventListener('scroll', update, { passive: true });
    new MutationObserver(update).observe(body, { childList: true, subtree: true });
    update();
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const target = event.target;

      const removeBtn = target.closest('[data-action="remove"]');
      if (removeBtn) {
        event.preventDefault();
        const item = removeBtn.closest('.slide-cart__item');
        const line = parseInt(item.dataset.line, 10);
        this.updateQuantity(line, 0);
        return;
      }

      const decreaseBtn = target.closest('[data-action="decrease"]');
      if (decreaseBtn) {
        event.preventDefault();
        const input = decreaseBtn
          .closest('.slide-cart__item-quantity')
          .querySelector('.slide-cart__item-quantity-input');
        const newVal = Math.max(0, parseInt(input.value, 10) - 1);
        input.value = newVal;
        this.debouncedUpdate(parseInt(input.dataset.line, 10), newVal);
        return;
      }

      const increaseBtn = target.closest('[data-action="increase"]');
      if (increaseBtn) {
        event.preventDefault();
        const input = increaseBtn
          .closest('.slide-cart__item-quantity')
          .querySelector('.slide-cart__item-quantity-input');
        const newVal = parseInt(input.value, 10) + 1;
        input.value = newVal;
        this.debouncedUpdate(parseInt(input.dataset.line, 10), newVal);
        return;
      }

      const discountToggle = target.closest('#SlideCart-DiscountToggle');
      if (discountToggle) {
        event.preventDefault();
        const section = this.querySelector('#SlideCart-DiscountSection');
        if (section && !section.classList.contains('is-expanded')) {
          this.openDiscountForm();
          const input = section.querySelector('.slide-cart__discount-input');
          if (input) input.focus();
        }
        return;
      }

      const discountClose = target.closest('#SlideCart-DiscountClose');
      if (discountClose) {
        event.preventDefault();
        this.closeDiscountForm();
        return;
      }

      const discountBtn = target.closest('#SlideCart-DiscountBtn');
      if (discountBtn) {
        event.preventDefault();
        this.applyDiscount();
        return;
      }

      const removeDiscountBtn = target.closest('[data-action="remove-discount"]');
      if (removeDiscountBtn) {
        event.preventDefault();
        this.removeDiscount(removeDiscountBtn.dataset.discountCode);
        return;
      }
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.closest('#SlideCart-DiscountInput')) {
        event.preventDefault();
        this.applyDiscount();
      }
    });

    this.addEventListener('change', (event) => {
      const input = event.target.closest('.slide-cart__item-quantity-input');
      if (!input) return;
      const line = parseInt(input.dataset.line, 10);
      const quantity = parseInt(input.value, 10);
      this.debouncedUpdate(line, Math.max(0, quantity));
    });
  }

  bindCartIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open();
      }
    });
  }

  open() {
    if (this.refreshPromise) {
      this.setGlobalLoading(true);
      this.refreshPromise.finally(() => this.setGlobalLoading(false));
    }
    if (window.modalHandler) {
      window.modalHandler.showModal('SlideCart');
    }
  }

  close() {
    if (window.modalHandler) {
      window.modalHandler.hideModal('SlideCart');
    }
  }

  subscribeToCartUpdates() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'slide-cart') {
          this.showNotification(event.cartAction);
          return;
        }

        if (event.cartData?.sections) {
          this.renderSections(event.cartData);
          this.scrollToTop();
          this.showNotification(event.cartAction);
        } else {
          this.refreshCart().finally(() => {
            this.showNotification(event.cartAction);
          });
        }

        if (event.source === 'product-form') {
          this.open();
        }
      });
    }
  }

  debouncedUpdate(line, quantity) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateQuantity(line, quantity);
    }, 300);
  }

  updateQuantity(line, quantity) {
    this.setItemsLoading(true);

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: ['slide-cart', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(window.routes.cart_change_url + '.js', {
      ...fetchConfig(),
      body: body,
    })
      .then((response) => response.json())
      .then((state) => {
        const errorMessage = state.errors || (state.status === 422 && state.description);
        if (errorMessage) {
          this.showItemError(line, errorMessage);
          this.resetQuantityInput(line);
          this.setItemsLoading(false);
          return;
        }

        this.clearItemErrors();
        this.renderSections(state, { refreshRebuy: quantity === 0 });
        this.syncGift(state);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'slide-cart',
            cartAction: quantity === 0 ? 'remove' : 'update',
            cartData: state,
          });
        }
      })
      .catch((error) => {
        console.error('SlideCart update error:', error);
      })
      .finally(() => {
        this.setItemsLoading(false);
      });
  }

  refreshCart() {
    this.refreshPromise = fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sections: ['slide-cart', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.json())
      .then((state) => {
        this.renderSections(state);
        this.syncGift(state);
        this.scrollToTop();
      })
      .catch((error) => {
        console.error('SlideCart refresh error:', error);
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  scrollToTop() {
    const body = this.modal.querySelector('.slide-cart__body');
    if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
  }

  renderHTML(html, { refreshRebuy = true } = {}) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const snippets = parsed.querySelectorAll('[data-cart-snippet]');

    if (snippets.length === 0) {
      const newModal = parsed.getElementById('SlideCart');
      if (newModal) this.modal.innerHTML = newModal.innerHTML;
      return;
    }

    snippets.forEach((newSnippet) => {
      const key = newSnippet.getAttribute('data-cart-snippet');
      const current = this.modal.querySelector(`[data-cart-snippet="${key}"]`);
      if (current) {
        current.replaceWith(newSnippet.cloneNode(true));
      }
    });

    // Update rebuy cart data and refresh recommendations
    const newCartData = parsed.getElementById('SlideCart-Rebuy-CartData');
    if (newCartData) {
      const currentCartData = document.getElementById('SlideCart-Rebuy-CartData');
      if (currentCartData) {
        currentCartData.textContent = newCartData.textContent;
      }
    }
    if (refreshRebuy && typeof window.refreshSlideCartRebuy === 'function') {
      window.refreshSlideCartRebuy();
    }
  }

  renderSections(state, options) {
    if (state.sections && state.sections['slide-cart']) {
      this.renderHTML(state.sections['slide-cart'], options);
    }

    if (state.sections && state.sections['cart-icon-bubble']) {
      const iconBubble = document.getElementById('cart-icon-bubble');
      if (iconBubble) {
        const parsed = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
        const newIcon = parsed.querySelector('.shopify-section');
        if (newIcon) iconBubble.innerHTML = newIcon.innerHTML;
      }
    }
  }

  openDiscountForm() {
    const section = this.querySelector('#SlideCart-DiscountSection');
    const toggle = this.querySelector('#SlideCart-DiscountToggle');
    if (section) section.classList.add('is-expanded');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  closeDiscountForm() {
    const section = this.querySelector('#SlideCart-DiscountSection');
    const toggle = this.querySelector('#SlideCart-DiscountToggle');
    if (section) section.classList.remove('is-expanded');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  applyDiscount() {
    const input = this.querySelector('#SlideCart-DiscountInput');
    const code = input ? input.value.trim() : '';

    if (!code) return;

    const existingCodes = Array.from(this.querySelectorAll('[data-discount-code]')).map(
      (btn) => btn.dataset.discountCode
    );
    const allCodes = [code, ...existingCodes].join(',');

    fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: allCodes,
        sections: ['slide-cart', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((cart) => {
        this.renderSections(cart);

        const hasInvalid = cart.discount_codes?.some((d) => !d.applicable);
        if (hasInvalid) {
          this.openDiscountForm();
          const msg = this.querySelector('#SlideCart-DiscountMessage');
          if (msg) {
            msg.textContent = msg.dataset.invalid;
            msg.classList.add('slide-cart__discount-message--error');
          }
        }
      })
      .catch((error) => {
        console.error('SlideCart discount error:', error);
      });
  }

  removeDiscount(code) {
    const allCodes = Array.from(this.querySelectorAll('[data-discount-code]'))
      .map((btn) => btn.dataset.discountCode)
      .filter((c) => c !== code);

    fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: allCodes.join(','),
        sections: ['slide-cart', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((cart) => {
        this.renderSections(cart);
      })
      .catch((error) => {
        console.error('SlideCart remove discount error:', error);
      });
  }

  renderContents(parsedState) {
    this.renderSections(parsedState);
    this.open();
  }

  getSectionsToRender() {
    return [{ id: 'slide-cart' }, { id: 'cart-icon-bubble' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  showNotification(action) {
    this.modal.querySelectorAll('.slide-cart__notification').forEach((n) => n.classList.remove('show'));

    const el = this.modal.querySelector(`#SlideCartNotification-${action}`);
    if (!el) return;

    el.classList.add('show');
    clearTimeout(this._notificationTimer);
    this._notificationTimer = setTimeout(() => el.classList.remove('show'), 5000);
  }

  setItemsLoading(isLoading) {
    const body = this.modal.querySelector('[data-cart-snippet="items"]');
    if (!body) return;
    if (isLoading) {
      body.classList.add('slide-cart__body--loading');
    } else {
      body.classList.remove('slide-cart__body--loading');
    }
  }

  setGlobalLoading(isLoading) {
    const overlay = this.modal.querySelector('#SlideCart-Loading');
    if (!overlay) return;
    if (isLoading) {
      overlay.classList.add('show');
    } else {
      overlay.classList.remove('show');
    }
  }

  showItemError(line, message) {
    const errorEl = this.querySelector(`#SlideCart-ItemError-${line}`);
    if (errorEl) {
      errorEl.querySelector('.slide-cart__item-error-text').textContent = message;
      errorEl.classList.add('is-visible');
    }
  }

  clearItemErrors() {
    this.querySelectorAll('.slide-cart__item-error.is-visible').forEach((el) => {
      el.classList.remove('is-visible');
      el.querySelector('.slide-cart__item-error-text').textContent = '';
    });
  }

  resetQuantityInput(line) {
    const input = this.querySelector(`.slide-cart__item-quantity-input[data-line="${line}"]`);
    if (input) {
      fetch(window.routes.cart_url + '.js')
        .then((r) => r.json())
        .then((cart) => {
          const item = cart.items[line - 1];
          if (item) input.value = item.quantity;
        });
    }
  }

  syncGift(cartData) {
    console.log('Syncing gift with cart data:', cartData);
    const giftVariantId = this.dataset.giftVariantId;
    const giftThreshold = parseInt(this.dataset.giftThreshold || '0', 10);

    console.log('Gift sync config:', { giftVariantId, giftThreshold });
    if (!giftVariantId || !giftThreshold || this.giftUpdating) return;

    const giftInCart = cartData.items.find(
      (item) => String(item.variant_id) === giftVariantId && item.properties && item.properties._gift === 'true'
    );

    console.log('Gift sync:', {
      giftInCart,
      total: cartData.total_price,
      shouldHaveGift: cartData.total_price >= giftThreshold,
    });

    const shouldHaveGift = cartData.total_price >= giftThreshold;

    if (shouldHaveGift && !giftInCart) {
      this.giftUpdating = true;
      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(giftVariantId, 10), quantity: 1, properties: { _gift: 'true' } }],
          sections: ['slide-cart', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          this.renderSections(state);
        })
        .catch((e) => console.error('SlideCart gift add error:', e))
        .finally(() => {
          this.giftUpdating = false;
        });
    } else if (!shouldHaveGift && giftInCart) {
      this.giftUpdating = true;
      const line = cartData.items.indexOf(giftInCart) + 1;
      fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: 0,
          sections: ['slide-cart', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          this.renderSections(state);
        })
        .catch((e) => console.error('SlideCart gift remove error:', e))
        .finally(() => {
          this.giftUpdating = false;
        });
    }
  }
}

customElements.define('slide-cart', SlideCart);
